import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';

import { signOut } from 'firebase/auth';

import { signUpWithEmail, signInWithGoogle, scaffoldNewAccount } from '@/lib/auth';
import { AuthConsentNotice } from '@/components/AuthConsentNotice';
import { AccountSetupProblem } from '@/components/AccountSetupProblem';
import { auth as firebaseAuth } from '@/lib/firebase';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { AuthLayout, Divider } from '@/components/AuthLayout';
import { InviteAuthHint } from '@/components/InviteAuthHint';
import { useAuth } from '@/hooks/useAuth';

const FRIENDLY: Record<string, string> = {
  'auth/email-already-in-use': 'An account already exists for this email.',
  'auth/weak-password': 'Pick a password with at least 6 characters.',
  'auth/invalid-email': "That doesn't look like a valid email.",
  'auth/network-request-failed': 'Network problem — try again in a moment.',
};

export function SignupPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Preserve the post-auth destination across signup. Used when a
  // brand-new caregiver lands on /add-button?code=… (or any other
  // auth-gated URL) — they get bounced via /login and the "Create
  // an account" link forwards `next` through to here so the device
  // claim picks up where it was. Falls back to the default RootRedirect.
  // Account-scoped destinations are stripped: a brand-new user can't
  // be a member of a pre-existing /accounts/{aid}/* page, so a
  // forwarded `next=/accounts/...` value (eg. from a stale RequireAuth
  // bounce on someone else's URL) would land them on a page they
  // shouldn't see — a brand-new user has no membership there, so it
  // would just bounce on the server's permission check.
  const rawNext = params.get('next') ?? '/';
  const next = rawNext.startsWith('/accounts/') ? '/' : rawNext;
  // Arriving from an invite link → steer to Google (pre-verified, skips the
  // email-verification step accept_invite requires).
  const isInvite = next.startsWith('/invites/');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The auth user is created FIRST, then ensure_user_profile scaffolds the
  // account. If that second step fails (App Check low score, cold start, network)
  // the auth user exists but has no account — keep the caregiver here with a
  // retry instead of bouncing them to the no-recovery "Nothing to manage yet"
  // dead-end (RootRedirect). Matches the fallback on Welcome / SetupDevice.
  const [scaffoldFailed, setScaffoldFailed] = useState(false);
  const [scaffoldMethod, setScaffoldMethod] = useState<'email' | 'google'>('email');
  const [retrying, setRetrying] = useState(false);

  // Already signed in (e.g. user opened /signup in a tab where they're
  // already authenticated)? Redirect. We deliberately do NOT navigate
  // on the auth.state transition mid-submit: signInWithPopup's
  // onAuthStateChanged fires the moment the popup resolves, BEFORE
  // ensureUserProfileCallable has finished writing the docs. If we
  // navigated then, the destination page (e.g. SetupDevice) would
  // mount with no memberships and spin forever while the orphan
  // callable completes in the background. The submit handlers below
  // navigate explicitly after the callable resolves.
  useEffect(() => {
    if (auth.state === 'signedIn' && !busy && !scaffoldFailed) {
      navigate(next, { replace: true });
    }
  }, [auth.state, navigate, next, busy, scaffoldFailed]);

  if (auth.state === 'signedIn' && !busy && !scaffoldFailed) {
    // Effect above will navigate; render nothing in the meantime.
    return null;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Ensure no prior session leaks into the new account. The
      // redirect-when-signedIn effect above generally catches this,
      // but it has a race during the initial auth-state 'loading'
      // window — if a stale Firebase Auth session is in IndexedDB,
      // the form is briefly visible and a fast submit can race past
      // the redirect. createUserWithEmailAndPassword would then
      // *replace* the current user mid-flight, leaving Firestore's
      // persistent cache populated with the prior user's documents
      // — which downstream stale-key snapshots can momentarily
      // render. Explicit signOut closes that window.
      if (firebaseAuth.currentUser) await signOut(firebaseAuth);
      await signUpWithEmail(email, password);
      navigate(next, { replace: true });
    } catch (e) {
      // If the auth user was created but scaffolding the account failed, the user
      // is signed in with no account — don't bounce to the dead-end landing; keep
      // them here with a retry (below). Otherwise it's an auth-creation failure
      // (email-in-use, weak password, …) → show the friendly message.
      if (firebaseAuth.currentUser) {
        setScaffoldMethod('email');
        setScaffoldFailed(true);
      } else {
        const code = e instanceof FirebaseError ? e.code : null;
        setError((code && FRIENDLY[code]) || (e instanceof Error ? e.message : 'Sign up failed.'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function retryScaffold() {
    if (!firebaseAuth.currentUser) return;
    setRetrying(true);
    try {
      await scaffoldNewAccount(firebaseAuth.currentUser, scaffoldMethod);
      setScaffoldFailed(false);
      navigate(next, { replace: true });
    } catch {
      // Stay on the retry screen; the button re-enables for another attempt.
    } finally {
      setRetrying(false);
    }
  }

  async function onGoogle() {
    setBusy(true);
    setError(null);
    try {
      // Same prior-session guard as onSubmit above.
      if (firebaseAuth.currentUser) await signOut(firebaseAuth);
      await signInWithGoogle();
      navigate(next, { replace: true });
    } catch (e) {
      // A new Google user whose account scaffold failed is signed in with no
      // account — offer the retry instead of bouncing to the dead-end landing.
      if (firebaseAuth.currentUser) {
        setScaffoldMethod('google');
        setScaffoldFailed(true);
      } else {
        setError(e instanceof Error ? e.message : 'Google sign-in failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  const googleButton = (
    <Button variant={isInvite ? 'primary' : 'secondary'} onClick={onGoogle} disabled={busy}>
      Continue with Google
    </Button>
  );

  // Auth succeeded but the account never scaffolded — offer a retry instead of a
  // dead-end. Reachable only after a caught scaffold failure (see onSubmit).
  if (scaffoldFailed) {
    return (
      <AuthLayout title="Almost there">
        <AccountSetupProblem onRetry={retryScaffold} retrying={retrying} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={isInvite ? 'Accept your invitation' : 'Create your free account'}
      subtitle={
        isInvite ? (
          <>Create your account to accept the invite and start helping with reminders.</>
        ) : (
          <>
            It&rsquo;s free to set up. Create your account, then customise the voice and
            today&rsquo;s reminders in the app and hear how they sound. If it&rsquo;s a good
            fit, you can order the button later from inside the app — no card needed to start.
          </>
        )
      }
    >
      {isInvite && (
        <>
          <InviteAuthHint />
          {googleButton}
          <Divider label="or use email" />
        </>
      )}
      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <Field label="Email" htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Password" htmlFor="password" hint="At least 6 characters.">
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}
        <Button type="submit" variant={isInvite ? 'secondary' : 'primary'} disabled={busy}>
          {busy ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
      {!isInvite && (
        <>
          <Divider />
          {googleButton}
        </>
      )}
      <AuthConsentNotice />
      <p className="mt-6 text-sm text-ink-muted">
        Already have an account?{' '}
        <Link
          to={`/login${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`}
          className="font-medium text-accent-deep hover:underline"
        >
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
